import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AR_SECTOR, arMap } from "@/i18n_halal";
import { PublicHeader } from "@/components/PublicHeader";
import { ArrowLeft, ArrowRight, Trophy, ShieldCheck, Medal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANK_TONE = ["bg-[#86A230] text-[#3A2E05]", "bg-[#C0C6CC] text-[#2B3138]",
                   "bg-[#C08457] text-[#2E1B0C]"];

const Row = ({ r, onOpen, t }) => (
  <button onClick={() => onOpen(r.symbol)}
    className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-muted/50 transition-colors text-start"
    data-testid={`leader-row-${r.symbol}`}>
    <span className={`h-6 w-6 rounded-lg grid place-items-center text-[11px] font-bold shrink-0 ${
      RANK_TONE[r.rank - 1] || "bg-secondary text-muted-foreground"}`}>{r.rank}</span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold truncate">{r.symbol}</span>
      <span className="block text-[11px] text-muted-foreground truncate">{r.name}</span>
    </span>
    <span className="text-end shrink-0">
      <span className="block numeric text-xs font-semibold">{r.purification_pct}% {t("lb_impure")}</span>
      <span className="block text-[10px] text-muted-foreground numeric">{t("lb_debt")} {r.debt_ratio ?? "—"}%</span>
    </span>
  </button>
);

export default function Leaderboards() {
  const navigate = useNavigate();
  const { t, lang } = useApp();
  const ar = lang === "ar";
  const [market, setMarket] = useState("All");
  const [d, setD] = useState(null);

  useEffect(() => {
    setD(null);
    axios.get(`${API}/public/halal-leaderboards`, { params: { market } })
      .then((r) => setD(r.data)).catch(() => {});
  }, [market]);

  const open = (symbol) => navigate(`/halal-stocks/${encodeURIComponent(symbol)}`);
  const slabel = (s) => (ar ? arMap(AR_SECTOR, s) : s);

  return (
    <div className="min-h-screen bg-background" data-testid="leaderboards-page">
      <PublicHeader />
      <div className="navy-strip text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
          <div className="mb-7 flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}
              className="text-white/70 hover:text-white hover:bg-white/10" data-testid="leaderboards-back">
              <ArrowLeft className="h-4 w-4 me-1.5" />{t("hl_back")}
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[color:var(--tharwy-lime)]">
            <Trophy className="h-3.5 w-3.5" />{t("lb_eyebrow")}
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="leaderboards-title">
            {t("lb_h1a")}<br />
            <span className="text-white/70">{t("lb_h1b")}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/70 leading-relaxed">
            {ar || !d?.ranked_by ? t("lb_ranked_default") : `${d.ranked_by}.`}
            {" "}{t("lb_sub_tail")}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Select value={market} onValueChange={setMarket}>
              <SelectTrigger className="h-10 w-[190px] bg-white/10 border-white/20 text-white" data-testid="leaderboards-market">
                <SelectValue placeholder={t("hl_market")} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="All">{t("hl_all_markets")}</SelectItem>
                {(d?.markets || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            {d && (
              <div className="flex flex-wrap gap-3" data-testid="leaderboards-totals">
                {[[t("lb_screened"), d.totals.screened.toLocaleString()],
                  [t("lb_compliant"), d.totals.compliant.toLocaleString()],
                  [t("lb_pass_rate"), `${d.totals.compliant_pct}%`]].map(([k, v]) => (
                  <div key={k} className="rounded-2xl bg-white/5 border border-white/10 px-5 py-2.5">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">{k}</div>
                    <div className="numeric text-base font-bold">{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
        {!d ? (
          <p className="text-sm text-muted-foreground">{t("lb_loading")}</p>
        ) : (
          <>
            {/* overall podium */}
            <Card className="p-5 sm:p-6 shadow-premium" data-testid="leaderboards-overall">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Medal className="h-4 w-4 text-[#86A230]" />{t("lb_cleanest_ten")}
                <span className="text-[11px] font-normal text-muted-foreground">
                  · {market === "All" ? t("lb_every_market") : market}
                </span>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {d.overall.map((r) => (
                  <button key={r.symbol} onClick={() => open(r.symbol)}
                    className="text-start rounded-2xl border border-border p-4 hover:shadow-premium transition-shadow"
                    data-testid={`overall-${r.symbol}`}>
                    <span className={`inline-grid h-6 w-6 place-items-center rounded-lg text-[11px] font-bold ${
                      RANK_TONE[r.rank - 1] || "bg-secondary text-muted-foreground"}`}>{r.rank}</span>
                    <div className="mt-2.5 text-sm font-bold">{r.symbol}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{r.name}</div>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                      <span>{r.market}</span>
                      <span className="numeric">{r.purification_pct}% {t("lb_impure")}</span>
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            {/* sectors */}
            <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leaderboards-sectors">
              {d.sectors.map((s) => (
                <Card key={s.sector} className="p-5 shadow-premium" data-testid={`sector-${s.sector.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-base md:text-lg font-semibold">{slabel(s.sector)}</h2>
                      <p className="text-[11px] text-muted-foreground">
                        {s.compliant} {t("lb_of")} {s.screened} {t("lb_pass_of")} {s.avg_debt_ratio ?? "—"}%
                      </p>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                      <ShieldCheck className="h-3 w-3" />{s.compliant_pct}%
                    </span>
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${s.compliant_pct}%` }} />
                  </div>
                  <div className="mt-3 -mx-1.5">
                    {s.leaders.map((r) => <Row key={r.symbol} r={r} onOpen={open} t={t} />)}
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Button className="rounded-full px-6" onClick={() => navigate("/top-halal-stocks")}
                data-testid="leaderboards-screener-cta">
                {t("lb_full_screener")} <ArrowRight className="h-4 w-4 ms-1.5" />
              </Button>
              <Button variant="outline" className="rounded-full px-6" onClick={() => navigate("/shariah-board")}
                data-testid="leaderboards-board-cta">{t("lb_how_we_screen")}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
